import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { toast } from "sonner";
import { Save, Loader2, Sparkles } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function PersonalizerPanel() {
  const { active } = useServer();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/personalizer/${active.id}`).then((r) => setCfg(r.data));
  }, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;
  if (!cfg) return <div className="text-zinc-500 font-mono-tabular text-sm">LOADING…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/features/personalizer/${active.id}`, cfg);
      toast.success("Personality applied");
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="personalizer-panel" className="space-y-8 max-w-3xl">
      <div>
        <div className="overline mb-2"><Sparkles className="inline w-4 h-4 mr-2 -mt-1" />MAIN</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Bot Personalizer</h1>
        <p className="text-zinc-400 mt-2">Customize how the bot appears: nickname, status, activity, brand color.</p>
      </div>

      <div className="tactical-card p-6 space-y-4">
        <div>
          <label className="overline block mb-2">Nickname (in this server only)</label>
          <input className="tactical-input" value={cfg.nickname || ""} onChange={(e) => set("nickname", e.target.value)} placeholder="606 Bot" data-testid="bp-nick" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Activity type</label>
            <select className="tactical-input" value={cfg.activity_type} onChange={(e) => set("activity_type", e.target.value)} data-testid="bp-atype">
              <option value="playing">Playing</option>
              <option value="listening">Listening to</option>
              <option value="watching">Watching</option>
              <option value="streaming">Streaming</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Status</label>
            <select className="tactical-input" value={cfg.status} onChange={(e) => set("status", e.target.value)} data-testid="bp-status">
              <option value="online">Online</option>
              <option value="idle">Idle</option>
              <option value="dnd">Do Not Disturb</option>
              <option value="invisible">Invisible</option>
            </select>
          </div>
        </div>
        <div>
          <label className="overline block mb-2">Activity text</label>
          <input className="tactical-input" value={cfg.activity_text} onChange={(e) => set("activity_text", e.target.value)} data-testid="bp-text" />
        </div>
        <div>
          <label className="overline block mb-2">Brand color (used in embeds)</label>
          <input type="color" className="tactical-input h-10 p-1" value={cfg.embed_color} onChange={(e) => set("embed_color", e.target.value)} data-testid="bp-color" />
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="bp-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
